import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import React from 'react';

export interface IPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  showEntriesInfo?: boolean;
  showPageInfo?: boolean;
}

export const Pagination: React.FC<IPaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  className = '',
  showEntriesInfo = true,
  showPageInfo = true,
}) => {
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const currentPageShow = totalItems === 0 ? 0 : currentPage;

  const getVisiblePages = () => {
    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const showControls = totalPages > 1;
  const visiblePages = showControls ? getVisiblePages() : [];
  const hasLeftInfo = showEntriesInfo || showPageInfo;

  return (
    <div
      className={`flex flex-wrap items-center gap-3 px-6 py-4 border-t border-theme-200 w-full min-w-0 ${
        hasLeftInfo ? 'justify-between' : 'justify-end'
      } ${className}`}
    >
      {hasLeftInfo && (
        <div className='flex flex-wrap items-center gap-4 text-sm text-gray-600 min-w-0'>
          {showEntriesInfo && (
            <div>
              Showing {startItem} to {endItem} of {totalItems} entries
            </div>
          )}
          {showPageInfo && (
            <div>
              Page {currentPageShow} of {totalPages}
            </div>
          )}
        </div>
      )}

      {showControls && (
        <div className='flex items-center gap-1 shrink-0'>
          {/* First Page */}
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className='p-1.5 text-gray-500 hover:text-gray-800 rounded-md hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            aria-label='Go to first page'
          >
            <ChevronsLeft className='w-4 h-4' />
          </button>

          {/* Previous Page */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className='p-1.5 text-gray-500 hover:text-gray-800 rounded-md hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            aria-label='Go to previous page'
          >
            <ChevronLeft className='w-4 h-4' />
          </button>

          {/* Page Numbers */}
          {visiblePages.map((page, index) => (
            <React.Fragment key={index}>
              {page === '...' ? (
                <span className='px-2 py-1.5 text-gray-400'>...</span>
              ) : (
                <button
                  onClick={() => handlePageChange(page as number)}
                  type='button'
                  className={`min-w-8 px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    currentPage === page
                      ? 'bg-theme-500 text-white'
                      : 'text-gray-700 hover:bg-black/5'
                  }`}
                  aria-label={`Go to page ${page}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              )}
            </React.Fragment>
          ))}

          {/* Next Page */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className='p-1.5 text-gray-500 hover:text-gray-800 rounded-md hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            aria-label='Go to next page'
          >
            <ChevronRight className='w-4 h-4' />
          </button>

          {/* Last Page */}
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className='p-1.5 text-gray-500 hover:text-gray-800 rounded-md hover:bg-black/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
            aria-label='Go to last page'
          >
            <ChevronsRight className='w-4 h-4' />
          </button>
        </div>
      )}
    </div>
  );
};
